
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { getOracleInterpretation, createOracleChat, testConnection } from './services/aiService.ts';
import { HexagramResult, OracleResponse, ChatMessage, DivinationRecord } from './types.ts';
import HexagramView from './components/HexagramView.tsx';

/**
 * 格式化文本组件
 * 自动识别换行并处理数字列表 (1. 2. 3. 等)
 */
const FormattedText: React.FC<{ text: string; className?: string; itemClassName?: string }> = ({ text, className, itemClassName }) => {
  if (!text) return null;
  
  // 按照换行符拆分，并过滤掉多余的空行
  const lines = text.split('\n').filter(line => line.trim() !== '');

  return (
    <div className={`space-y-3 ${className}`}>
      {lines.map((line, index) => {
        // 检测是否是以数字开头的列表项 (例如 "1. " 或 "1、")
        const isListItem = /^\d+[\.、\s]/.test(line.trim());
        
        return (
          <p 
            key={index} 
            className={`leading-relaxed animate-fade-in ${isListItem ? `pl-6 -indent-6 ${itemClassName}` : ''}`}
            style={{ animationDelay: `${index * 0.05}s` }}
          >
            {line}
          </p>
        );
      })}
    </div>
  );
};

// API 配置模态框组件
const ApiConfigModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (keys: { deepseek?: string; gemini?: string }) => void;
  currentKeys: { deepseek?: string; gemini?: string };
}> = ({ isOpen, onClose, onUpdate, currentKeys }) => {
  const [dsKey, setDsKey] = useState(currentKeys.deepseek || '');
  const [gmKey, setGmKey] = useState(currentKeys.gemini || '');
  const [dsStatus, setDsStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [gmStatus, setGmStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');

  useEffect(() => {
    if (isOpen) {
      const storedDs = localStorage.getItem('ds_key') || '';
      const storedGm = localStorage.getItem('gm_key') || '';
      setDsKey(storedDs);
      setGmKey(storedGm);
      setDsStatus(storedDs ? 'success' : 'idle');
      setGmStatus(storedGm ? 'success' : 'idle');
    }
  }, [isOpen]);

  const verifyKey = async (provider: 'gemini' | 'deepseek') => {
    const key = provider === 'gemini' ? gmKey : dsKey;
    if (!key) return;

    const setStatus = provider === 'gemini' ? setGmStatus : setDsStatus;
    setStatus('testing');

    const isValid = await testConnection(provider, key);
    if (isValid) {
      setStatus('success');
      localStorage.setItem(`${provider === 'gemini' ? 'gm' : 'ds'}_key`, key);
      onUpdate({
        gemini: provider === 'gemini' ? key : gmKey,
        deepseek: provider === 'deepseek' ? key : dsKey
      });
    } else {
      setStatus('error');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in">
      <div className="glass-card w-full max-w-md rounded-3xl p-8 border-amber-500/30 relative overflow-hidden shadow-[0_0_100px_rgba(245,158,11,0.1)]">
        <div className="absolute top-0 right-0 p-4">
          <button onClick={onClose} className="text-slate-500 hover:text-amber-400 transition-colors text-2xl">×</button>
        </div>
        
        <div className="text-center mb-6">
          <h2 className="title-font text-2xl text-amber-400 tracking-widest">通灵密钥配置</h2>
          <p className="text-[10px] text-slate-500 mt-2 tracking-widest uppercase italic">密钥存储于您的浏览器本地</p>
        </div>

        <div className="space-y-5">
          <div className="space-y-2">
            <div className="flex justify-between items-center px-1">
              <label className="text-[10px] text-amber-200/60 font-bold tracking-widest uppercase">DeepSeek Key (必填)</label>
              <span className={`text-[9px] font-bold transition-all ${dsStatus === 'success' ? 'text-green-500' : dsStatus === 'error' ? 'text-red-500' : 'text-slate-600'}`}>
                {dsStatus === 'success' ? '● 已连接' : dsStatus === 'error' ? '● 校验失败' : dsStatus === 'testing' ? '校验中...' : '○ 未连接'}
              </span>
            </div>
            <div className="flex gap-2">
              <input 
                type="password" 
                value={dsKey}
                onChange={(e) => { setDsKey(e.target.value); setDsStatus('idle'); }}
                placeholder="sk-..."
                className="flex-1 bg-black/60 border border-slate-800 rounded-xl p-3 text-xs text-amber-400 outline-none focus:border-amber-500/50"
              />
              <button 
                onClick={() => verifyKey('deepseek')}
                className="px-4 bg-amber-600/20 hover:bg-amber-600/40 text-amber-400 border border-amber-500/30 rounded-xl text-[10px] font-bold transition-all"
              >
                校验
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center px-1">
              <label className="text-[10px] text-amber-200/60 font-bold tracking-widest uppercase">Gemini Key (可选)</label>
              <span className={`text-[9px] font-bold transition-all ${gmStatus === 'success' ? 'text-green-500' : gmStatus === 'error' ? 'text-red-500' : 'text-slate-600'}`}>
                {gmStatus === 'success' ? '● 已连接' : gmStatus === 'error' ? '● 校验失败' : gmStatus === 'testing' ? '校验中...' : '○ 未连接'}
              </span>
            </div>
            <div className="flex gap-2">
              <input 
                type="password" 
                value={gmKey}
                onChange={(e) => { setGmKey(e.target.value); setGmStatus('idle'); }}
                placeholder="AIza..."
                className="flex-1 bg-black/60 border border-slate-800 rounded-xl p-3 text-xs text-amber-400 outline-none focus:border-amber-500/50"
              />
              <button 
                onClick={() => verifyKey('gemini')}
                className="px-4 bg-amber-600/20 hover:bg-amber-600/40 text-amber-400 border border-amber-500/30 rounded-xl text-[10px] font-bold transition-all"
              >
                校验
              </button>
            </div>
          </div>
        </div>

        <button 
          onClick={onClose}
          className="w-full mt-8 py-4 bg-amber-600 hover:bg-amber-500 text-white rounded-xl text-xs font-black tracking-[0.3em] transition-all shadow-lg active:scale-95"
        >
          完成并返回
        </button>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<{ deepseek?: string; gemini?: string }>({
    deepseek: localStorage.getItem('ds_key') || undefined,
    gemini: localStorage.getItem('gm_key') || undefined
  });
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [history, setHistory] = useState<DivinationRecord[]>([]);
  
  const [numA, setNumA] = useState<string>('');
  const [numB, setNumB] = useState<string>('');
  const [numC, setNumC] = useState<string>('');
  const [question, setQuestion] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingMsgIndex, setLoadingMsgIndex] = useState(0);
  const [hexagram, setHexagram] = useState<HexagramResult | null>(null);
  const [oracle, setOracle] = useState<OracleResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeSession, setActiveSession] = useState<any | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [userInput, setUserInput] = useState('');
  const [isChatting, setIsChatting] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const loadingMessages = [
    "正在观测数字脉络...",
    "拨动阴阳，推演乾坤...",
    "查阅《易经》古籍...",
    "感应变爻之契机...",
    "大师正在静心入定...",
    "凝结神谕智慧中..."
  ];

  useEffect(() => {
    let timer: number;
    if (isLoading) {
      timer = window.setInterval(() => {
        setLoadingMsgIndex(prev => (prev + 1) % loadingMessages.length);
      }, 2500);
    } else {
      setLoadingMsgIndex(0);
    }
    return () => clearInterval(timer);
  }, [isLoading]);

  useEffect(() => {
    const saved = localStorage.getItem('oracle_history');
    if (saved) {
      try { setHistory(JSON.parse(saved)); } catch (e) { console.error(e); }
    }
  }, []);

  const saveToHistory = (record: DivinationRecord) => {
    const newHistory = [record, ...history].slice(0, 50);
    setHistory(newHistory);
    localStorage.setItem('oracle_history', JSON.stringify(newHistory));
  };

  const scrollToBottom = () => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(() => { if (messages.length > 0) scrollToBottom(); }, [messages, isChatting]);

  const handleDivination = useCallback(async () => {
    const currentDsKey = apiKeys.deepseek || localStorage.getItem('ds_key');
    const currentGmKey = apiKeys.gemini || localStorage.getItem('gm_key');
    
    if (!currentDsKey) {
      setError('请先配置 DeepSeek API 密钥。');
      setIsConfigOpen(true);
      return;
    }
    if (!numA || !numB || !numC || !question) {
      setError('起卦需填写完整的三组数字。');
      return;
    }
    setError(null);
    setIsLoading(true);
    setHexagram(null);
    setOracle(null);
    setMessages([]);
    try {
      const result = await getOracleInterpretation(
        question, 
        { a: parseInt(numA), b: parseInt(numB), c: parseInt(numC) }, 
        currentDsKey, 
        currentGmKey || ''
      );
      
      const session = createOracleChat(question, result.facts, result.hexagram, currentDsKey);
      
      setHexagram(result.hexagram);
      setOracle({
        facts: result.facts,
        masterInterpretation: result.masterResponse
      });
      setActiveSession(session);

      saveToHistory({
        id: Date.now().toString(),
        timestamp: Date.now(),
        question,
        originalHexagram: result.hexagram.originalHexagramName,
        transformedHexagram: result.hexagram.transformedHexagramName,
        summary: result.masterResponse.advice.slice(0, 45) + '...'
      });
    } catch (err: any) {
      setError(err.message || '灵力感应中断。');
    } finally {
      setIsLoading(false);
    }
  }, [numA, numB, numC, question, apiKeys, history]);

  const handleSendMessage = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!userInput.trim() || !activeSession || isChatting) return;
    const userText = userInput.trim();
    setUserInput('');
    setMessages(prev => [...prev, { role: 'user', text: userText }]);
    setIsChatting(true);
    try {
      const result = await activeSession.sendMessage({ message: userText });
      const responseText = result.text || (result.choices?.[0]?.message?.content) || "大师静思，暂无回音。";
      setMessages(prev => [...prev, { role: 'model', text: responseText }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'model', text: "灵力不稳。" }]);
    } finally {
      setIsChatting(false);
    }
  };

  const downloadHistory = () => {
    const blob = new Blob([JSON.stringify(history, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `神谕历史.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const isFormValid = numA.length === 3 && numB.length === 3 && numC.length === 3 && question.trim().length > 0;

  return (
    <div className="relative min-h-screen z-10 p-3 md:p-6 flex flex-col items-center pt-4 md:pt-6 selection:bg-amber-500/40">
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[60vh] md:text-[80vh] text-amber-500/[0.08] pointer-events-none select-none z-0 animate-[spin_300s_linear_infinite] filter blur-[3px]">☯️</div>
      
      <div className="fixed top-3 left-3 z-[80]">
        <button 
          onClick={() => setIsSidebarOpen(true)}
          className="flex items-center gap-2 px-3 py-2 rounded-full glass-card border-amber-500/20 text-amber-400 hover:bg-amber-500/15 transition-all shadow-lg"
        >
          <span className="text-lg">📜</span>
          <span className="text-[9px] font-black tracking-widest uppercase font-serif hidden sm:inline">神谕簿</span>
        </button>
      </div>

      <div className="fixed top-3 right-3 z-[80]">
        <button 
          onClick={() => setIsConfigOpen(true)}
          className={`flex items-center gap-2 px-3 py-2 rounded-full border transition-all text-[9px] font-black tracking-widest uppercase shadow-md ${
            apiKeys.deepseek ? 'bg-green-500/5 border-green-500/30 text-green-500' : 'bg-amber-500/10 border-amber-500/40 text-amber-400'
          }`}
        >
          <span className={`block w-1.5 h-1.5 rounded-full ${apiKeys.deepseek ? 'bg-green-500' : 'bg-amber-500 animate-pulse'}`}></span>
          <span>{apiKeys.deepseek ? '已通灵' : '配置密钥'}</span>
        </button>
      </div>

      <div className={`fixed inset-y-0 left-0 z-[110] w-72 max-w-[85vw] glass-card border-r border-amber-500/20 transition-transform duration-500 ease-in-out shadow-2xl flex flex-col ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-4 border-b border-amber-500/10 flex justify-between items-center bg-black/40">
          <h3 className="title-font text-lg text-amber-400">往昔感应</h3>
          <button onClick={() => setIsSidebarOpen(false)} className="text-slate-500 hover:text-amber-400 text-xl">×</button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {history.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full opacity-20"><p className="text-[10px] uppercase font-serif">尚未起卦</p></div>
          ) : (
            history.map((rec) => (
              <div key={rec.id} className="p-3 rounded-lg bg-white/5 border border-slate-800/50 hover:border-amber-500/20 transition-all cursor-default">
                <div className="text-[7px] text-amber-500/30 mb-1 font-mono">{new Date(rec.timestamp).toLocaleString()}</div>
                <div className="text-[11px] text-white/80 line-clamp-1 font-serif italic mb-1">“{rec.question}”</div>
                <div className="text-[9px] font-black text-amber-400/40 flex justify-between">
                  <span>{rec.originalHexagram} → {rec.transformedHexagram}</span>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="p-3 bg-black/40 border-t border-amber-500/10">
          <button onClick={downloadHistory} disabled={history.length === 0} className="w-full py-2.5 rounded-lg bg-amber-600/10 hover:bg-amber-600/20 text-amber-400 border border-amber-500/20 text-[9px] font-black tracking-widest uppercase disabled:opacity-30">下载记录</button>
        </div>
      </div>
      {isSidebarOpen && <div className="fixed inset-0 z-[105] bg-black/60 backdrop-blur-sm" onClick={() => setIsSidebarOpen(false)} />}

      <ApiConfigModal isOpen={isConfigOpen} onClose={() => setIsConfigOpen(false)} onUpdate={setApiKeys} currentKeys={apiKeys} />
      
      <header className="text-center mb-4 md:mb-6 relative z-10 animate-fade-in w-full max-w-2xl px-6">
        <h1 className="title-font text-2xl md:text-4xl text-amber-400 mb-1 tracking-[0.1em] drop-shadow-[0_0_15px_rgba(251,191,36,0.5)]">周易数字神谕</h1>
        <div className="flex items-center justify-center gap-4">
          <div className="h-[1px] w-8 bg-amber-900/30"></div>
          <p className="text-slate-400 text-[9px] md:text-xs italic font-serif tracking-[0.4em] uppercase font-bold opacity-80">易理乾坤 · 数字指津</p>
          <div className="h-[1px] w-8 bg-amber-900/30"></div>
        </div>
      </header>

      <main className="w-full max-w-4xl relative z-10">
        {isLoading ? (
          <div className="py-16 flex flex-col items-center justify-center gap-8 animate-fade-in min-h-[350px]">
            <div className="relative">
              <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border border-amber-500/10 rotate-slow flex items-center justify-center">
                <div className="w-full h-full border-t border-amber-500/40 rounded-full animate-spin"></div>
              </div>
              <div className="absolute inset-0 m-auto w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-amber-500/10 rounded-full energy-glow">
                <span className="text-2xl md:text-3xl">☯️</span>
              </div>
            </div>
            <p key={loadingMsgIndex} className="text-amber-400 text-xs md:text-sm tracking-[0.3em] font-serif uppercase animate-fade-in font-bold">
              {loadingMessages[loadingMsgIndex]}
            </p>
          </div>
        ) : !hexagram ? (
          <div className="animate-fade-in space-y-4">
            <div className="glass-card rounded-2xl p-5 md:p-8 space-y-5 border-amber-500/15 shadow-xl">
              <div className="grid grid-cols-3 gap-3 md:gap-6">
                {[{label: '下卦', val: numA, set: setNumA}, {label: '上卦', val: numB, set: setNumB}, {label: '变爻', val: numC, set: setNumC}].map((input, i) => (
                  <div key={i} className="flex flex-col gap-1.5 group">
                    <label className="text-[9px] text-slate-500 font-black uppercase tracking-[0.2em] text-center">{input.label}</label>
                    <input 
                      type="text" 
                      inputMode="numeric" 
                      value={input.val} 
                      onChange={(e) => input.set(e.target.value.replace(/\D/g, '').slice(0, 3))} 
                      placeholder="000" 
                      className={`bg-black/50 border ${input.val.length === 3 ? 'border-amber-400/50' : 'border-slate-800'} rounded-lg p-2.5 md:p-4 text-center text-2xl md:text-3xl outline-none text-amber-400 font-mono focus:border-amber-500/60 transition-all w-full shadow-inner`} 
                    />
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-1.5 group">
                <label className="text-[9px] text-slate-500 font-black uppercase tracking-[0.2em]">心中困惑</label>
                <textarea 
                  value={question} 
                  onChange={(e) => setQuestion(e.target.value)} 
                  placeholder="请输入您的困惑..." 
                  className="bg-black/50 border border-slate-800 rounded-lg p-3 h-20 text-white outline-none resize-none font-serif text-base focus:border-amber-500/60 transition-all" 
                />
              </div>
              {error && <p className="text-red-400 text-[10px] text-center font-bold">✷ {error}</p>}
              <button 
                onClick={handleDivination} 
                disabled={!isFormValid}
                className={`w-full py-3 rounded-lg text-lg font-black transition-all shadow-lg tracking-[0.3em] ${isFormValid ? 'bg-amber-600 hover:bg-amber-500 text-white' : 'bg-slate-900/80 text-slate-600 cursor-not-allowed border border-slate-800'}`}
              >
                起卦寻芳
              </button>
            </div>
            
            <div className="text-center px-6 py-4 rounded-xl border border-amber-500/40 bg-amber-500/10 shadow-[0_0_20px_rgba(245,158,11,0.1)] transition-all animate-fade-in">
              <p className="text-amber-100 text-[12px] md:text-sm leading-relaxed font-serif italic max-w-md mx-auto drop-shadow-md">
                请闭目凝神，静心想要询问的烦心事情，<br/>输入三组 <span className="text-amber-400 font-black underline underline-offset-4 decoration-amber-500/50">三位数字</span>。<br/>
                <span className="text-[10px] md:text-[11px] text-amber-400/80 uppercase tracking-[0.2em] mt-2 block font-bold">心诚则灵，大师将为您解析宇宙之理</span>
              </p>
            </div>
          </div>
        ) : (
          <div className="w-full px-1 pb-16 space-y-8 animate-fade-in">
            <section className="glass-card rounded-3xl p-6 md:p-8 flex flex-row justify-around items-center border-amber-500/10 shadow-xl relative overflow-hidden">
              <HexagramView lines={hexagram.originalLines} changingIndex={hexagram.changingLineIndex} title={hexagram.originalHexagramName} />
              <div className="text-amber-400/40 font-black text-[10px] uppercase tracking-widest text-center flex flex-col items-center">
                <div className="h-8 w-[1px] bg-amber-500/20 mb-2"></div>
                变<br/>第{hexagram.changingLineIndex}爻
                <div className="h-8 w-[1px] bg-amber-500/20 mt-2"></div>
              </div>
              <HexagramView lines={hexagram.transformedLines} title={hexagram.transformedHexagramName} />
            </section>

            {oracle && (
              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="glass-card p-5 rounded-2xl border-slate-800/40">
                    <h5 className="text-amber-500/30 text-[9px] font-black uppercase tracking-widest mb-3 flex items-center gap-2"><span>《卦辞》</span><div className="h-[1px] flex-1 bg-slate-800/50"></div></h5>
                    <FormattedText text={oracle.facts.hexagramText} className="text-white font-serif text-lg leading-snug" />
                  </div>
                  <div className="glass-card p-5 rounded-2xl border-slate-800/40">
                    <h5 className="text-amber-500/30 text-[9px] font-black uppercase tracking-widest mb-3 flex items-center gap-2"><span>《爻辞》</span><div className="h-[1px] flex-1 bg-slate-800/50"></div></h5>
                    <FormattedText text={oracle.facts.changingLineText} className="text-white font-serif text-lg leading-snug" />
                  </div>
                </div>

                <div className="relative group max-w-4xl mx-auto">
                  <div className="absolute -inset-1 bg-gradient-to-r from-amber-600/10 via-amber-400/5 to-amber-900/10 rounded-[2rem] blur-xl opacity-30"></div>
                  <div className="relative bg-[#020617]/95 p-8 md:p-12 rounded-[2rem] border border-amber-500/20 shadow-2xl overflow-hidden">
                    <h3 className="text-amber-400 title-font text-3xl md:text-4xl mb-8 text-center tracking-[0.2em] relative z-10">周易大师神谕</h3>
                    <div className="space-y-6 relative z-10">
                      <section className="pl-5 border-l border-amber-500/20">
                        <h4 className="text-amber-200/50 text-[10px] font-black tracking-widest uppercase mb-2">现状分析</h4>
                        <FormattedText text={oracle.masterInterpretation.situation} className="text-slate-300 text-base font-serif italic" />
                      </section>
                      <section className="pl-5 border-l border-amber-500/20">
                        <h4 className="text-amber-200/50 text-[10px] font-black tracking-widest uppercase mb-2">易理启示</h4>
                        <FormattedText text={oracle.masterInterpretation.insight} className="text-slate-100 text-lg font-serif" />
                      </section>
                      <section className="bg-amber-600/5 p-6 md:p-8 rounded-2xl border border-amber-500/15 shadow-inner relative overflow-hidden">
                        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#f59e0b 0.5px, transparent 0.5px)', backgroundSize: '16px 16px' }}></div>
                        <h4 className="text-amber-400 text-[10px] font-black tracking-widest uppercase mb-3 relative z-10">行动指南</h4>
                        <FormattedText 
                          text={oracle.masterInterpretation.advice} 
                          className="text-amber-50 font-serif text-lg relative z-10" 
                          itemClassName="text-amber-200/80 font-bold"
                        />
                      </section>
                    </div>
                  </div>
                </div>

                <div className="pt-4 max-w-2xl mx-auto">
                  <div className="glass-card rounded-[1.5rem] h-[350px] flex flex-col shadow-xl overflow-hidden border-amber-500/15">
                    <div className="flex-1 overflow-y-auto p-5 space-y-5 bg-black/20 text-sm">
                      {messages.map((msg, i) => (
                        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}>
                          <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 shadow-md ${msg.role === 'user' ? 'bg-amber-600/15 text-white border border-amber-500/20' : 'bg-slate-800/80 text-slate-100 border border-slate-700/50 font-serif'}`}>
                            <FormattedText text={msg.text} className="space-y-1.5" />
                          </div>
                        </div>
                      ))}
                      {isChatting && <div className="text-amber-500/30 text-[9px] italic animate-pulse font-serif">大师正在感应...</div>}
                      <div ref={chatEndRef} />
                    </div>
                    <form onSubmit={handleSendMessage} className="p-3 bg-slate-900/60 border-t border-slate-800/50 flex gap-2">
                      <input type="text" value={userInput} onChange={(e) => setUserInput(e.target.value)} placeholder="追问大师..." className="flex-1 bg-black/40 border border-slate-700 rounded-xl px-4 py-2 text-xs outline-none text-white focus:border-amber-500/30 transition-all" disabled={isChatting} />
                      <button type="submit" disabled={isChatting || !userInput.trim()} className="px-5 bg-amber-600/80 hover:bg-amber-600 text-white rounded-xl text-xs font-black transition-all shadow-md uppercase">求教</button>
                    </form>
                  </div>
                </div>
                <div className="text-center"><button onClick={() => { setHexagram(null); setOracle(null); }} className="text-amber-500/20 hover:text-amber-400 transition-all text-[9px] tracking-[0.5em] uppercase font-black py-4">重新起卦 ↺</button></div>
              </div>
            )}
          </div>
        )}
      </main>
      
      <footer className="mt-12 pb-8 w-full max-w-2xl px-4 animate-fade-in" style={{ animationDelay: '0.5s' }}>
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="h-[1px] w-full bg-gradient-to-r from-transparent via-amber-500/10 to-transparent"></div>
          <div className="py-3 px-6 rounded-[2rem] border border-amber-500/20 bg-amber-500/[0.03] shadow-inner">
            <p className="text-amber-400/90 text-[10px] md:text-xs font-black tracking-[0.2em] uppercase drop-shadow-sm">
              可联系作者QQ1563245
            </p>
            <p className="text-amber-400/70 text-[9px] md:text-[11px] font-bold tracking-[0.15em] mt-1.5">
              预测改运、编写代码、运动强身
            </p>
            <p className="text-amber-400/60 text-[9px] md:text-[10px] font-medium tracking-[0.1em] mt-1.5 opacity-80">
              微信公众号：无限玄世界
            </p>
          </div>
          <div className="h-[1px] w-full bg-gradient-to-r from-transparent via-amber-500/10 to-transparent"></div>
        </div>
      </footer>
    </div>
  );
};

export default App;
